<script setup lang="ts">
import type { Presentation } from '~/models/Presentation/Presentation'
import type { PresentationPage } from '~/models/Presentation/BasePage'
import { useRouter }  from '#imports'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { PendoFeature } from '~/models/Pendo'
import useSlugify from '~/composables/useSlugify'
import ScrollBox from '~/components/utils/ScrollBox.vue'
import Button from '~/components/ui/Button.vue'
import AuPopover from '~/components/ui/AuPopover.vue'

const props = defineProps<{
  pageNumber: number
  pages: PresentationPage[]
  presentation: Presentation
}>()

const router = useRouter()
const { t } = useI18n()
const { slugify } = useSlugify()

const currentPage = computed(() => props.pages[props.pageNumber - 1])
const colorPair = computed(() => currentPage.value.colorPair)

const generateRoute = (pageNumber: number) => {
  let path = router.resolve({ name: 'presentation-page', params: {
    locationId: props.presentation.locationId,
    slug: slugify(props.presentation.title),
    pageNumber,
  } }).href
  if (router.currentRoute.value.params?.listId) {
    path = router.resolve({
      name: 'favorite_list_navigation',
      params: { listId: router.currentRoute.value.params.listId }
    }).href + path
  }
  return path
}
</script>

<template>
  <AuPopover
    side="top"
    align="end"
  >
    <template #trigger>
      <Button
        size="xl"
        variant="ghost"
        :aria-label="t('labels.presentation')"
        :data-pendo="PendoFeature.Presentation.Overview"
        :style="{ background: colorPair.background, color: colorPair.text, outlineColor: colorPair.text }"
        class="hover:not-disabled:!bg-(--hover-bg) hover:not-disabled:!text-(--hover-color)"
      >
        {{ t('presentation.pageNumberOfTotal', { pageNumber, totalPages: pages.length }) }}
      </Button>
    </template>
    <template #content>
      <div
        class="-m-2 grid max-h-[min(42rem,75vh)] max-w-screen min-w-96 grid-rows-[max-content_minmax(0,1fr)] rounded-xl border border-(--color)"
        :style="{
          '--color': colorPair.background,
          '--background': colorPair.text,
          'background': 'var(--background)',
          'color': 'var(--color)',
        }"
      >
        <div class="flex flex-row justify-between">
          <header
            class="p-6 pt-8 text-3xl font-semibold"
            v-text="t('presentation.menu.content')"
          />
        </div>

        <ScrollBox class="flex flex-col p-6 pt-0">
          <div v-if="pages.length > 1">
            <ul class="space-y-3">
              <li
                v-for="(page, index) in pages"
                :key="`contentMenu-${page.contentId}`"
                :aria-current="pageNumber === index + 1"
              >
                <Button
                  :to="generateRoute(index + 1)"
                  :class="{ 'bg-(--color) text-(--background)': pageNumber === index + 1 }"
                  full-width
                  :color="colorPair.background"
                  size="lg"
                >
                  <span
                    aria-hidden="true"
                    v-text="index + 1"
                  />
                  <p class="truncate" v-text="page.shortTitle || page.title || t('presentation.pageNumber', { pageNumber: index + 1 })" />
                </Button>
              </li>
            </ul>
          </div>
        </ScrollBox>
      </div>
    </template>
  </AuPopover>
</template>
